import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useMotionValue, useTransform, PanInfo } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  Home, User, Heart, MessageCircle, Settings, Search, 
  ChevronUp, ChevronDown, Mic, MicOff, Vibrate, 
  Navigation, Compass, Camera, Phone, Video,
  ArrowLeft, ArrowRight, MoreHorizontal, Bell
} from 'lucide-react';

// Mobile Bottom Navigation Bar
export const MobileBottomNav = ({ activeTab, onTabChange, className = "" }) => {
  const [hapticEnabled, setHapticEnabled] = useState(true);

  const tabs = [
    { id: 'home', icon: <Home className="w-5 h-5" />, label: 'Home', route: '/' },
    { id: 'search', icon: <Search className="w-5 h-5" />, label: 'Discover', route: '/discover' },
    { id: 'dating', icon: <Heart className="w-5 h-5" />, label: 'Dating', route: '/speed-dating' },
    { id: 'messages', icon: <MessageCircle className="w-5 h-5" />, label: 'Messages', route: '/messages' },
    { id: 'profile', icon: <User className="w-5 h-5" />, label: 'Profile', route: '/profile' }
  ];

  const triggerHaptic = () => {
    if (hapticEnabled && 'vibrate' in navigator) {
      navigator.vibrate(50);
    }
  };

  const handleTabPress = (tab) => {
    triggerHaptic();
    onTabChange(tab);
  };

  return (
    <motion.div 
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      className={`fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-lg ${className}`}
    >
      <div className="flex items-center justify-around py-2 px-4 max-w-md mx-auto">
        {tabs.map((tab, index) => (
          <motion.button
            key={tab.id}
            onClick={() => handleTabPress(tab)}
            className={`flex flex-col items-center py-2 px-3 rounded-lg transition-all ${
              activeTab === tab.id 
                ? 'text-rose-500' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
            whileTap={{ scale: 0.95 }}
            animate={{
              scale: activeTab === tab.id ? 1.1 : 1,
              transition: { type: "spring", stiffness: 300 }
            }}
          >
            <motion.div
              animate={{
                color: activeTab === tab.id ? '#ec4899' : '#6b7280',
                transition: { duration: 0.2 }
              }}
            >
              {tab.icon}
            </motion.div>
            <span className={`text-xs mt-1 ${
              activeTab === tab.id ? 'text-rose-500 font-medium' : 'text-gray-500'
            }`}>
              {tab.label}
            </span>
            
            {activeTab === tab.id && (
              <motion.div
                layoutId="activeTab"
                className="absolute -top-1 w-8 h-1 bg-rose-500 rounded-full"
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
              />
            )}
          </motion.button>
        ))}
      </div>
      
      {/* iPhone Home Indicator */}
      <div className="h-1 bg-gray-300 rounded-full w-32 mx-auto mb-2"></div>
    </motion.div>
  );
};

// Swipe Gesture Card Component
export const SwipeableCard = ({ 
  children, 
  onSwipeLeft, 
  onSwipeRight, 
  onSwipeUp,
  onSwipeDown,
  enableHaptic = true,
  swipeThreshold = 100,
  className = ""
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  
  const rotateX = useTransform(x, [-200, 200], [-15, 15]);
  const rotateY = useTransform(y, [-200, 200], [10, -10]);
  const opacity = useTransform(x, [-200, -100, 0, 100, 200], [0.5, 0.8, 1, 0.8, 0.5]);

  const triggerHaptic = (type = 'light') => {
    if (enableHaptic && 'vibrate' in navigator) {
      const patterns = {
        light: 50,
        medium: 100,
        heavy: 200
      };
      navigator.vibrate(patterns[type]);
    }
  };

  const handleDragEnd = (event, info) => {
    setIsDragging(false);
    
    const threshold = swipeThreshold;
    const { offset } = info;

    if (Math.abs(offset.x) > threshold) {
      triggerHaptic('medium');
      if (offset.x > 0 && onSwipeRight) {
        onSwipeRight();
      } else if (offset.x < 0 && onSwipeLeft) {
        onSwipeLeft();
      }
    } else if (Math.abs(offset.y) > threshold) {
      triggerHaptic('light');
      if (offset.y < 0 && onSwipeUp) {
        onSwipeUp();
      } else if (offset.y > 0 && onSwipeDown) {
        onSwipeDown();
      }
    }

    // Reset position
    x.set(0);
    y.set(0);
  };

  return (
    <motion.div
      className={`relative cursor-grab active:cursor-grabbing ${className}`}
      drag
      dragConstraints={{ left: 0, right: 0, top: 0, bottom: 0 }}
      dragElastic={0.2}
      onDragStart={() => {
        setIsDragging(true);
        triggerHaptic('light');
      }}
      onDragEnd={handleDragEnd}
      style={{ 
        x, 
        y, 
        rotateX, 
        rotateY, 
        opacity,
        transformStyle: 'preserve-3d'
      }}
      whileDrag={{ 
        scale: 0.95,
        transition: { duration: 0.1 }
      }}
    >
      {children}
      
      {isDragging && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="absolute inset-0 border-2 border-rose-300 rounded-lg pointer-events-none"
        />
      )}
    </motion.div>
  );
};

// Voice Input Component
export const VoiceInput = ({ 
  onTranscript, 
  placeholder = "Tap to speak...", 
  className = "",
  maxDuration = 60000 // 1 minute
}) => {
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [isSupported, setIsSupported] = useState(false);
  const recognitionRef = useRef(null);
  const timeoutRef = useRef(null);

  useEffect(() => {
    // Check for browser support
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      setIsSupported(true);
      recognitionRef.current = new SpeechRecognition();
      
      const recognition = recognitionRef.current;
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'en-US';

      recognition.onstart = () => {
        setIsListening(true);
        // Auto-stop after max duration
        timeoutRef.current = setTimeout(() => {
          recognition.stop();
        }, maxDuration);
      };

      recognition.onresult = (event) => {
        let finalTranscript = '';
        let interimTranscript = '';

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript;
          } else {
            interimTranscript += transcript;
          }
        }

        const fullTranscript = finalTranscript || interimTranscript;
        setTranscript(fullTranscript);
        if (finalTranscript && onTranscript) {
          onTranscript(finalTranscript);
        }
      };

      recognition.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };

      recognition.onend = () => {
        setIsListening(false);
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [maxDuration, onTranscript]);

  const startListening = () => {
    if (recognitionRef.current && !isListening) {
      setTranscript('');
      recognitionRef.current.start();
      
      // Haptic feedback
      if ('vibrate' in navigator) {
        navigator.vibrate(100);
      }
    }
  };

  const stopListening = () => {
    if (recognitionRef.current && isListening) {
      recognitionRef.current.stop();
    }
  };

  if (!isSupported) {
    return (
      <div className={`text-center p-4 text-gray-500 ${className}`}>
        <Mic className="w-6 h-6 mx-auto mb-2 opacity-50" />
        <p className="text-sm">Voice input not supported in this browser</p>
      </div>
    );
  }

  return (
    <motion.div className={`${className}`}>
      <motion.button
        onClick={isListening ? stopListening : startListening}
        className={`flex items-center justify-center space-x-3 w-full p-4 rounded-lg border-2 transition-all ${
          isListening 
            ? 'border-rose-500 bg-rose-50 text-rose-600' 
            : 'border-gray-300 bg-white text-gray-600 hover:border-gray-400'
        }`}
        whileTap={{ scale: 0.98 }}
        animate={{
          scale: isListening ? [1, 1.02, 1] : 1,
          transition: { 
            duration: isListening ? 1 : 0,
            repeat: isListening ? Infinity : 0
          }
        }}
      >
        <motion.div
          animate={{
            rotate: isListening ? 360 : 0,
            transition: { 
              duration: 2, 
              repeat: isListening ? Infinity : 0,
              ease: "linear" 
            }
          }}
        >
          {isListening ? (
            <MicOff className="w-5 h-5" />
          ) : (
            <Mic className="w-5 h-5" />
          )}
        </motion.div>
        
        <div className="flex-1 text-left">
          {isListening ? (
            <div>
              <p className="font-medium">Listening...</p>
              {transcript && (
                <p className="text-sm opacity-75 truncate">{transcript}</p>
              )}
            </div>
          ) : (
            <p className="font-medium">{placeholder}</p>
          )}
        </div>
        
        {isListening && (
          <motion.div
            className="flex space-x-1"
            animate={{ opacity: [0.3, 1, 0.3] }}
            transition={{ duration: 1, repeat: Infinity }}
          >
            <div className="w-1 h-4 bg-rose-500 rounded"></div>
            <div className="w-1 h-4 bg-rose-500 rounded"></div>
            <div className="w-1 h-4 bg-rose-500 rounded"></div>
          </motion.div>
        )}
      </motion.button>
      
      {transcript && !isListening && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-3 p-3 bg-gray-50 rounded-lg border"
        >
          <p className="text-sm text-gray-700">{transcript}</p>
        </motion.div>
      )}
    </motion.div>
  );
};

// One-Handed Floating Action Button
export const FloatingActionButton = ({ 
  actions = [], 
  className = "",
  position = "bottom-right" 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const positions = {
    'bottom-right': 'bottom-20 right-4',
    'bottom-left': 'bottom-20 left-4',
    'bottom-center': 'bottom-20 left-1/2 transform -translate-x-1/2'
  };

  const triggerHaptic = () => {
    if ('vibrate' in navigator) {
      navigator.vibrate(50);
    }
  };

  const toggleExpanded = () => {
    triggerHaptic();
    setIsExpanded(!isExpanded);
  };

  return (
    <div className={`fixed ${positions[position]} z-50 ${className}`}>
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 20 }}
            className="space-y-3 mb-3"
          >
            {actions.map((action, index) => (
              <motion.button
                key={index}
                initial={{ opacity: 0, x: 20 }}
                animate={{ 
                  opacity: 1, 
                  x: 0,
                  transition: { delay: index * 0.1 }
                }}
                exit={{ opacity: 0, x: 20 }}
                onClick={() => {
                  triggerHaptic();
                  action.onClick();
                  setIsExpanded(false);
                }}
                className="flex items-center space-x-3 bg-white shadow-lg rounded-full py-3 px-4 border border-gray-200 hover:shadow-xl transition-all"
                whileTap={{ scale: 0.95 }}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${action.color || 'bg-gray-100'}`}>
                  {action.icon}
                </div>
                <span className="text-sm font-medium text-gray-700 whitespace-nowrap">
                  {action.label}
                </span>
              </motion.button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main FAB */}
      <motion.button
        onClick={toggleExpanded}
        className="w-14 h-14 bg-gradient-to-r from-rose-500 to-pink-500 rounded-full shadow-lg flex items-center justify-center text-white"
        whileTap={{ scale: 0.9 }}
        animate={{
          rotate: isExpanded ? 45 : 0,
          transition: { type: "spring", stiffness: 300 }
        }}
      >
        <MoreHorizontal className="w-6 h-6" />
      </motion.button>
    </div>
  );
};

// Touch Gesture Handler Hook
export const useTouchGestures = (elementRef, options = {}) => {
  const {
    onSwipeLeft,
    onSwipeRight,
    onSwipeUp,
    onSwipeDown,
    onPinch,
    onTap,
    onDoubleTap,
    onLongPress,
    threshold = 50,
    timeout = 300
  } = options;

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    let startTouch = null;
    let lastTap = 0;
    let longPressTimer = null;

    const handleTouchStart = (e) => {
      const touch = e.touches[0];
      startTouch = {
        x: touch.clientX,
        y: touch.clientY,
        time: Date.now()
      };

      // Long press detection
      if (onLongPress) {
        longPressTimer = setTimeout(() => {
          if ('vibrate' in navigator) navigator.vibrate(100);
          onLongPress(e);
        }, 500);
      }
    };

    const handleTouchMove = () => {
      // Clear long press if finger moves
      if (longPressTimer) {
        clearTimeout(longPressTimer);
        longPressTimer = null;
      }
    };

    const handleTouchEnd = (e) => {
      if (longPressTimer) {
        clearTimeout(longPressTimer);
        longPressTimer = null;
      }

      if (!startTouch) return;

      const endTouch = e.changedTouches[0];
      const deltaX = endTouch.clientX - startTouch.x;
      const deltaY = endTouch.clientY - startTouch.y;
      const deltaTime = Date.now() - startTouch.time;

      // Swipe detection
      if (Math.abs(deltaX) > threshold || Math.abs(deltaY) > threshold) {
        if ('vibrate' in navigator) navigator.vibrate(50);
        
        if (Math.abs(deltaX) > Math.abs(deltaY)) {
          // Horizontal swipe
          if (deltaX > 0 && onSwipeRight) {
            onSwipeRight(e);
          } else if (deltaX < 0 && onSwipeLeft) {
            onSwipeLeft(e);
          }
        } else {
          // Vertical swipe
          if (deltaY > 0 && onSwipeDown) {
            onSwipeDown(e);
          } else if (deltaY < 0 && onSwipeUp) {
            onSwipeUp(e);
          }
        }
      } else if (deltaTime < timeout) {
        // Tap detection
        const now = Date.now();
        const timeSinceLastTap = now - lastTap;

        if (timeSinceLastTap < 300 && timeSinceLastTap > 0) {
          // Double tap
          if (onDoubleTap) {
            if ('vibrate' in navigator) navigator.vibrate([50, 50, 50]);
            onDoubleTap(e);
          }
        } else {
          // Single tap
          if (onTap) {
            if ('vibrate' in navigator) navigator.vibrate(30);
            onTap(e);
          }
        }
        
        lastTap = now;
      }

      startTouch = null;
    };

    element.addEventListener('touchstart', handleTouchStart, { passive: false });
    element.addEventListener('touchmove', handleTouchMove, { passive: false });
    element.addEventListener('touchend', handleTouchEnd, { passive: false });

    return () => {
      element.removeEventListener('touchstart', handleTouchStart);
      element.removeEventListener('touchmove', handleTouchMove);
      element.removeEventListener('touchend', handleTouchEnd);
      
      if (longPressTimer) {
        clearTimeout(longPressTimer);
      }
    };
  }, [elementRef, onSwipeLeft, onSwipeRight, onSwipeUp, onSwipeDown, onTap, onDoubleTap, onLongPress, onPinch, threshold, timeout]);
};

export default {
  MobileBottomNav,
  SwipeableCard,
  VoiceInput,
  FloatingActionButton,
  useTouchGestures
};